/**
 * Notification events fired from app services. Event keys are string-typed
 * in the DB (no pgEnum) — add entries here to keep them typed across the
 * stack. Each event has a typed payload so dispatcher and consumers stay
 * in sync.
 */

export const NOTIFICATION_EVENTS = [
	"invoice.generated",
	"invoice.qbo_push_succeeded",
	"invoice.qbo_push_failed",
	"qbo.token_expired",
	"qbo.customers_need_review",
	"document.extraction_failed",
	"document.low_confidence",
	"document.uploaded_by_other",
	"user.invited",
	"user.accepted_invite",
	"user.two_factor_reset_by_admin",
] as const;

export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number];

/** Per-event payload shapes. Keep fields small + human-readable. */
export type NotificationPayloads = {
	"invoice.generated": {
		invoiceId: string;
		customerName: string;
		totalCents: number;
		period: string;
	};
	"invoice.qbo_push_succeeded": {
		invoiceId: string;
		customerName: string;
		qboDocNumber?: string;
		totalCents: number;
	};
	"invoice.qbo_push_failed": {
		invoiceId: string;
		customerName: string;
		error: string;
	};
	"qbo.token_expired": {
		realmId: string;
		companyName?: string;
	};
	"qbo.customers_need_review": {
		pendingCount: number;
	};
	"document.extraction_failed": {
		documentId: string;
		filename: string;
		error: string;
	};
	"document.low_confidence": {
		documentId: string;
		filename: string;
		reasons: string[];
	};
	"document.uploaded_by_other": {
		documentId: string;
		filename: string;
		uploaderName: string;
	};
	"user.invited": {
		invitationId: string;
		email: string;
		role: string;
	};
	"user.accepted_invite": {
		userId: string;
		email: string;
		name: string;
	};
	"user.two_factor_reset_by_admin": {
		targetUserId: string;
		targetEmail: string;
		resetByUserName: string;
	};
};

export type PayloadFor<E extends NotificationEvent> = NotificationPayloads[E];

/**
 * Category grouping for the notification settings UI. Keeps the toggle
 * list organized instead of one flat list.
 */
export const NOTIFICATION_CATEGORIES = {
	invoices: {
		label: "Invoices",
		events: ["invoice.generated", "invoice.qbo_push_succeeded", "invoice.qbo_push_failed"],
	},
	documents: {
		label: "Documents",
		events: ["document.extraction_failed", "document.low_confidence", "document.uploaded_by_other"],
	},
	quickbooks: {
		label: "QuickBooks",
		events: ["qbo.token_expired", "qbo.customers_need_review"],
	},
	team: {
		label: "Team",
		events: ["user.invited", "user.accepted_invite", "user.two_factor_reset_by_admin"],
	},
} as const satisfies Record<string, { label: string; events: readonly NotificationEvent[] }>;

/** Human-readable label for each event — used in the settings UI. */
export const NOTIFICATION_EVENT_LABELS: Record<NotificationEvent, string> = {
	"invoice.generated": "Invoice generated",
	"invoice.qbo_push_succeeded": "Invoice pushed to QuickBooks",
	"invoice.qbo_push_failed": "QuickBooks push failed",
	"qbo.token_expired": "QuickBooks connection expired",
	"qbo.customers_need_review": "QuickBooks customer changes need review",
	"document.extraction_failed": "Document extraction failed",
	"document.low_confidence": "Document needs attention",
	"document.uploaded_by_other": "New document uploaded by teammate",
	"user.invited": "User invited",
	"user.accepted_invite": "User accepted invite",
	"user.two_factor_reset_by_admin": "2FA reset by admin",
};

/** Short description shown under each toggle. */
export const NOTIFICATION_EVENT_DESCRIPTIONS: Record<NotificationEvent, string> = {
	"invoice.generated": "When an auto-invoice is drafted and ready for review.",
	"invoice.qbo_push_succeeded": "When an invoice is successfully synced to QuickBooks.",
	"invoice.qbo_push_failed": "When a QuickBooks push fails — usually requires action.",
	"qbo.token_expired": "When the QuickBooks OAuth connection needs reauthorization.",
	"qbo.customers_need_review":
		"When the nightly QuickBooks sync finds customer changes it won't apply on its own.",
	"document.extraction_failed": "When extraction from a source document errors out.",
	"document.low_confidence": "When extracted data is low confidence and may need review.",
	"document.uploaded_by_other": "When a teammate uploads a document.",
	"user.invited": "When you invite a new user (copy to you).",
	"user.accepted_invite": "When an invited user completes setup.",
	"user.two_factor_reset_by_admin": "When an admin resets 2FA for any user — security audit.",
};
